import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { BlogsDocument } from '~/gql'
import { shopifyFetch } from '~/utils'
import { Button } from '../Button'
import { useTranslations } from '../Locale'
import { SearchInput } from '../SearchInput'
import { ArticleCard, ArticleGrid } from './Articles'

export const Blogs: React.FC = () => {
	const blogsResponse = useQuery(['blogs'], () => shopifyFetch(BlogsDocument, {}))
	const t = useTranslations('react.blogs')
	const [search, setSearch] = useState('')
	const [sortBy, setSortBy] = useState('')

	const blogs = useMemo(() => (blogsResponse.data?.errors ? [] : (blogsResponse.data?.data ?? [])), [blogsResponse])

	const visibleArticles = useMemo(
		() =>
			blogs
				?.flatMap(blog => blog.articles.map(a => ({ ...a, blog })))
				.filter(article => {
					const searchLower = search.toLowerCase()
					return (
						article.title.toLowerCase().includes(searchLower) ||
						article.blog.title.toLowerCase().includes(searchLower) ||
						article.content?.toLowerCase().includes(searchLower)
					)
				})
				.sort((a, b) => {
					if (sortBy === 'oldest') {
						return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime()
					}

					return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
				}),
		[blogs, search, sortBy]
	)

	if (!blogs) {
		return null
	}
	return (
		<div className="page-width mt-20 space-y-12">
			<h1 className="text-center text-6xl">{t?.blogs}</h1>

			<div className="flex items-center justify-center gap-4 sm:gap-8">
				{blogs.map(blog => (
					<Button
						href={`${window.Shopify.routes.root}blogs/${blog.handle}`}
						key={blog.handle}
						className="px-4 py-2 text-2xl sm:px-10 sm:py-4 sm:text-3xl"
					>
						{blog.title}
					</Button>
				))}
			</div>
			<div className="mt-4 flex items-center gap-4">
				<SearchInput
					placeholder={t?.search}
					value={search}
					onChange={setSearch}
					className="!rounded-md !border !border-gray-300 !py-2 px-4"
					containerClassName="w-full"
				/>
				<select
					name="sort"
					value={sortBy}
					onChange={e => setSortBy(e.target.value)}
					className="!mb-0 rounded-md border border-gray-300 px-4 py-2"
				>
					<option value="" disabled>
						{t?.sort_by}
					</option>
					<option value="newest">{t?.newest}</option>
					<option value="oldest">{t?.oldest}</option>
				</select>
			</div>

			{visibleArticles.length === 0 ? (
				<p className="text-center">{t?.no_articles}</p>
			) : (
				<ArticleGrid>
					{visibleArticles.map(article => (
						<ArticleCard key={article.id} article={article} blog={article.blog} />
					))}
				</ArticleGrid>
			)}
		</div>
	)
}
